<svelte:options runes={true} />

<script lang="ts">
  import type { Command, CommandManager, ToolManager } from '@commands';
  import { type CommandId } from '@commands';
  import type { AppIcon } from '@ui';
  import { SlFontSize, SlTooltipPlacement } from '@ui';
  import { expectDefined } from '@utils';
  import CommandTooltip from './CommandTooltip.svelte';
  import Icon from './Icon.svelte';

  let {
    commandId,
    toolManager,
    commandManager
  }: {
    commandId: CommandId;
    toolManager: ToolManager;
    commandManager: CommandManager;
  } = $props();

  let foregroundColor = $derived.by(() => {
    if (commandId == toolManager.activeTool) {
      return 'var(--fl-tool-active-foreground)';
    } else if (
      commandId === toolManager.primaryTool &&
      toolManager.momentaryTool !== undefined
    ) {
      return 'var(--fl-tool-alt-foreground)';
    } else {
      return 'var(--fl-tool-inactive-foreground)';
    }
  });

  let backgroundColor = $derived.by(() => {
    if (commandId == toolManager.activeTool) {
      return 'var(--fl-tool-active-background)';
    } else if (
      commandId === toolManager.primaryTool &&
      toolManager.momentaryTool !== undefined
    ) {
      // Use active background as base; overlay supplies intermediate shade
      return 'var(--fl-tool-alt-background)';
    } else {
      return 'var(--fl-tool-inactive-background)';
    }
  });

  // Alt (primary tool while a momentary tool is engaged)
  let isAlt = $derived.by(() => {
    return (
      commandId === toolManager.primaryTool &&
      toolManager.momentaryTool !== undefined &&
      commandId !== toolManager.activeTool
    );
  });

  // Inactive state (not active and not alt)
  let isInactive = $derived.by(() => {
    return !(
      commandId == toolManager.activeTool ||
      (commandId === toolManager.primaryTool && toolManager.momentaryTool !== undefined)
    );
  });

  function handleClick(_event: MouseEvent) {
    toolManager.selectTool(commandId);
  }

  function command(): Command {
    return expectDefined(commandManager.getCommand(commandId));
  }

  function icon(): AppIcon {
    return expectDefined(command().icon);
  }
</script>

<style>
  .tool {
    padding: 4px 4px;
    border-radius: 4px;
    border-color: var(--sl-input-border-color);
    border-width: 1px;
    border-style: solid;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tool:hover {
    border-color: var(--fl-tool-border-hover);
  }

  /* Dimmed background overlay for inactive tools */
  .tool.inactive {
    position: relative;
  }
  .tool.inactive::before {
    content: '';
    position: absolute;
    inset: 0;
    /* background: var(--sl-color-neutral-0); */
    /* opacity: 0.35; */
    border-radius: 4px;
    pointer-events: none;
  }

  /* Alt state: intermediate shade between active (solid) and inactive (0.35 overlay) */
  .tool.alt {
    position: relative;
  }
  .tool.alt::before {
    content: '';
    position: absolute;
    inset: 0;
    /* background: var(--sl-color-neutral-0); */
    /* opacity: 0.18; */
    border-radius: 4px;
    pointer-events: none;
  }
</style>

{#snippet iconButton()}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    style="background-color: {backgroundColor}; color: {foregroundColor};"
    class="tool"
    class:alt={isAlt}
    class:inactive={isInactive}
    onclick={event => {
      handleClick(event);
    }}
  >
    <Icon icon={icon()} size={SlFontSize.LG} />
  </div>
{/snippet}

<CommandTooltip command={command()} placement={SlTooltipPlacement.BOTTOM}>
  {@render iconButton()}
</CommandTooltip>
